import { useCallback } from 'react';
import * as Sentry from '@sentry/react';

type BreadcrumbLevel = 'info' | 'warning' | 'error' | 'debug';
type MessageLevel = 'info' | 'warning' | 'error' | 'fatal' | 'debug';

const useSentry = () => {

  const trackUserClick = useCallback((
    elementName: string,
    additionalData: Record<string, any> = {}
  ) => {
    Sentry.addBreadcrumb({
      category: 'click',
      message: `User clicked: ${elementName}`,
      data: {
        elementName,
        ...additionalData,
        timestamp: new Date().toISOString()
      },
      level: 'info' as BreadcrumbLevel
    });
  }, []);

  const trackUserAction = useCallback((
    category: string,
    message: string,
    data: Record<string, any> = {}) => {
    Sentry.addBreadcrumb({
      category,
      message,
      data,
      level: 'info' as BreadcrumbLevel,
    });
  }, []);

  const captureException = useCallback((error: Error, context: string = '') => {
    Sentry.captureException(error, {
      tags: {
        handled: 'true',
        context,
      },
    });
  }, []);

  const captureMessage = useCallback((message: string, level: MessageLevel = 'info', extraData: Record<string, any> = {}) => {
    Sentry.captureMessage(message, {
      level,
      extra: extraData,
    });
  }, []);

  const setTag = useCallback((key: string, value: string) => {
    Sentry.setTag(key, value);
  }, []);

  const setTagScope = useCallback((key: string, value: string) => {
    Sentry.withScope((scope) => {
      scope.setTag(key, value);
    });
  }, []);

  const setContext = useCallback((name: string, data: Record<string, any>) => {
    Sentry.setContext(name, data);
  }, []);

  const setUser = useCallback((userData: {
    id?: string;
    email?: string;
    username?: string;
    [key: string]: any;
  } | null) => {
    Sentry.setUser(userData);
  }, []);

  return {
    trackUserAction,
    captureException,
    captureMessage,
    setTag,
    setContext,
    setUser,
    setTagScope,
    trackUserClick
  };
};

export default useSentry;